import React, { useState, useEffect, useRef } from 'react';
import { Send, Bot, User, Code, Loader2, ChevronRight, ChevronDown, Download, BarChart3, TrendingUp, Filter, DollarSign, Calendar, AlertTriangle, CheckCircle, Presentation } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import DataTable from '../components/DataTable';
import AssetGrid from '../components/AssetGrid';
import MessageVoting from '../components/MessageVoting';
import FeedbackModal from '../components/FeedbackModal';
import { reportData } from '../utils/buryReport';

const JobsiteDeepDive = () => {
  const { user, isAuthenticated } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState(0);
  const [followUpQuestions, setFollowUpQuestions] = useState([]);
  const [userVotes, setUserVotes] = useState({});
  const [feedbackModal, setFeedbackModal] = useState({ isOpen: false, messageId: null });
  const [selectedMetric, setSelectedMetric] = useState('cost');

  const hasReported = useRef(false); // 防止重复上报
  
  // Scroll to top when component mounts
  useEffect(() => {
    if (!hasReported.current) {
      reportData(1, 'Jobsite Deep Dive Page');
      hasReported.current = true;
    }
    window.scrollTo(0, 0);
  }, []);
  const [expandedSQL, setExpandedSQL] = useState({}); // Track which SQL sections are expanded
  const [defaultQuestions, setDefaultQuestions] = useState([]); // Store personalized questions from API
  const [selectedRole, setSelectedRole] = useState('Exec'); // Default to Executive
  const [isPersonalized, setIsPersonalized] = useState(false); // Track if questions are personalized
  const [userEmail, setUserEmail] = useState(''); // Store current user email
  const [gammaLoading, setGammaLoading] = useState({}); // Track loading state for Gamma slides generation
  const [gammaProgress, setGammaProgress] = useState({}); // Track progress for Gamma slides generation
  const messagesEndRef = useRef(null);

  // Role-specific Vista questions (1-2 per role)
  const roleQuestions = {
    'Exec': [
      "What is completion rate of project 25-480?"
    ],
    'Division Lead': [
      "What projects have the most unapproved invoices?"
    ],
    'Finance': [
      "Please list top 10 projects by total unpaid accounts receivables"
    ],
    'PM': [
      "Total unpaid billing amounts for project 25-480"
    ]
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch personalized Vista questions from API
  const fetchPersonalizedQuestions = async () => {
    try {
      // Use the actual authenticated user's email
      if (!isAuthenticated || !user?.email) {
        console.log('⚠️ No authenticated user, using default role-based questions');
        setDefaultQuestions(roleQuestions[selectedRole] || roleQuestions['PM']);
        setIsPersonalized(false);
        return;
      }
      
      const response = await axios.get('/api/personalized-questions/vista', {
        headers: {
          'X-User-Email': user.email,
          'X-User-Role': user.role || selectedRole
        }
      });
      
      if (response.data.success) {
        setDefaultQuestions(response.data.questions || []);
        setIsPersonalized(response.data.isPersonalized);
        setUserEmail(response.data.userEmail);
        
        console.log(`🎯 Loaded ${response.data.questions.length} questions for ${response.data.userEmail}`);
        console.log(`📋 Questions are ${response.data.isPersonalized ? 'personalized' : 'default role-based'}`);
      } else {
        throw new Error(response.data.error || 'Failed to fetch questions');
      }
    } catch (error) {
      console.error('Error fetching personalized questions:', error);
      // Fallback to role-based questions if API fails
      setDefaultQuestions(roleQuestions[selectedRole] || roleQuestions['PM']);
      setIsPersonalized(false);
      toast.error('Using default questions - personalized questions unavailable');
    }
  };

  // Fetch personalized questions when user changes (not when role changes)
  useEffect(() => {
    fetchPersonalizedQuestions();
  }, [user?.email, isAuthenticated]);

  // Handle role changes - only affects non-personalized users
  useEffect(() => {
    if (!isAuthenticated || !user?.email) {
      // No user - use role-based questions
      console.log(`🔄 No user, showing ${selectedRole} questions`);
      setDefaultQuestions(roleQuestions[selectedRole] || roleQuestions['PM']);
      setIsPersonalized(false);
    } else if (!isPersonalized) {
      // User exists but not personalized - update role-based questions
      console.log(`🔄 Non-personalized user, updating to ${selectedRole} questions`);
      setDefaultQuestions(roleQuestions[selectedRole] || roleQuestions['PM']);
    } else {
      // User is personalized - do nothing, keep their custom questions
      console.log(`✋ Personalized user, ignoring role change to ${selectedRole}`);
    }
  }, [selectedRole, isPersonalized, isAuthenticated, user?.email]);

  // Initialize with welcome message and default suggestions


  // Handle user voting on messages
  const handleVote = async (messageId, vote) => {
    setUserVotes(prev => ({ ...prev, [messageId]: vote }));
    
    try {
      await axios.post('/api/feedback/vote', {
        messageId,
        vote,
        timestamp: new Date().toISOString()
      });
      
      if (vote === 'up') {
        toast.success('Thank you for your feedback!');
      }
    } catch (error) {
      console.error('Error submitting vote:', error);
      toast.error('Failed to submit vote');
    }
  };

  // Handle feedback submission for thumbs down
  const handleFeedback = async (feedbackData) => {
    try {
      await axios.post('/api/feedback/submit', feedbackData);
      toast.success('Thank you for your detailed feedback!');
      closeFeedbackModal();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback');
    }
  };

  // Open feedback modal
  const openFeedbackModal = (messageId, voteType) => {
    setFeedbackModal({ isOpen: true, messageId, voteType });
  };

  // Close feedback modal
  const closeFeedbackModal = () => {
    setFeedbackModal({ isOpen: false, messageId: null });
  };

  // Export data to CSV
  const exportToCSV = (data, columns, messageContent) => {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      // Create CSV header
      const header = columns.join(',');
      
      // Create CSV rows
      const rows = data.map(row => 
        columns.map(col => {
          const value = row[col];
          // Handle values that contain commas, quotes, or newlines
          if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value || '';
        }).join(',')
      );
      
      // Combine header and rows
      const csvContent = [header, ...rows].join('\n');
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `jobsite-data-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Data exported to CSV successfully!');
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      toast.error('Failed to export data');
    }
  };

  // Gamma API integration for generating beautiful slides
  const generateGammaSlides = async (message, index) => {
    try {
      setGammaLoading(prev => ({ ...prev, [index]: true }));
      setGammaProgress(prev => ({ ...prev, [index]: { started: true } }));
      
      console.log('🎯 Starting Gamma slides generation for Jobsite...');
      
      // Calculate dynamic number of cards based on content length
      let inputText = '';
      
      if (message.content) {
        inputText += `## Summary\n${message.content}\n\n`;
      }
      
      if (message.sqlRationale) {
        inputText += `## SQL Analysis\n${message.sqlRationale}\n\n`;
      }
      
      if (message.data && message.data.length > 0) {
        inputText += `## Data Table\n`;
        if (message.columns) {
          inputText += `Columns: ${message.columns.join(', ')}\n\n`;
        }
        inputText += `Sample Data (first 5 rows):\n`;
        message.data.slice(0, 5).forEach((row, i) => {
          inputText += `Row ${i + 1}: ${Object.values(row).join(' | ')}\n`;
        });
        inputText += `\nTotal rows: ${message.data.length}\n\n`;
      }
      
      if (message.sqlQuery) {
        inputText += `## SQL Query\n${message.sqlQuery}\n\n`;
      }
      
      // Hardcode 5 slides for demo
      const numCards = 5;
      
      console.log(`📊 numCards: ${numCards} (hardcoded for demo)`);
      console.log('📝 Input text for Gamma:', inputText.substring(0, 200) + '...');
      
      toast.success(`🎯 Generating ${numCards} slides... This may take a few minutes.`);
      
      const response = await fetch('/api/jobsite/gamma-slides', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          inputText: inputText,
          numCards: numCards
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to start Gamma slides generation');
      }

      const generationId = data.generationId;
      console.log('🆔 Gamma generation started with ID:', generationId);
      
      // Start progress tracking with 5-minute timer for large slide decks
      const startTime = Date.now();
      const maxWaitTime = 300 * 1000; // 5 minutes for large slide decks
      
      const updateProgress = () => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, maxWaitTime - elapsed);
        const progressPercent = Math.min(95, (elapsed / maxWaitTime) * 95); // Cap at 95% until actual completion
        
        setGammaProgress(prev => ({
          ...prev,
          [index]: {
            ...prev[index],
            started: true,
            elapsed: Math.floor(elapsed / 1000),
            remaining: Math.floor(remaining / 1000),
            progress: progressPercent
          }
        }));
      };
      
      // Update progress every second
      const progressInterval = setInterval(updateProgress, 1000);
      updateProgress(); // Initial update
      
      // Check status every 10 seconds - no max attempts, poll indefinitely
      let attempts = 0;
      
      const pollForCompletion = async () => {
        try {
          attempts++;
          console.log(`🔍 Polling attempt ${attempts} for generation ${generationId}`);
          
          const statusResponse = await fetch(`/api/jobsite/gamma-slides/${generationId}`);
          if (statusResponse.ok) {
            const statusData = await statusResponse.json();
            
            if (statusData.success && statusData.status === 'completed') {
              clearInterval(progressInterval);
              clearInterval(pollInterval);
              
              setGammaProgress(prev => ({
                ...prev,
                [index]: { 
                  ...prev[index], 
                  completed: true,
                  progress: 100,
                  gammaUrl: statusData.gammaUrl,
                  exportUrl: statusData.exportUrl
                }
              }));
              
              setGammaLoading(prev => ({ ...prev, [index]: false }));
              toast.success('Gamma slides generated successfully!');
              
              // Update message with Gamma URLs
              setMessages(prev => prev.map((msg, i) => 
                i === index ? { ...msg, gammaUrl: statusData.gammaUrl, exportUrl: statusData.exportUrl } : msg
              ));
              return;
            }
          }
          
          // Keep polling indefinitely - no timeout
        } catch (error) {
          console.error('Error checking Gamma status:', error);
          // Keep polling even on error
        }
      };
      
      // Start polling immediately, then every 10 seconds
      pollForCompletion();
      const pollInterval = setInterval(pollForCompletion, 10000);
      
      // Keep polling indefinitely - no cleanup timeout
      // User can refresh page if needed, but we'll keep checking for completion
      
    } catch (error) {
      console.error('❌ Failed to generate Gamma slides:', error);
      toast.error('Failed to generate Gamma slides');
      setGammaLoading(prev => ({ ...prev, [index]: false }));
      setGammaProgress(prev => ({
        ...prev,
        [index]: { 
          ...prev[index], 
          completed: true, 
          error: error.message 
        }
      }));
    }
  };

  // Helper function to get export icon
  const getExportIcon = (url) => {
    if (!url) return '📄';
    if (url.includes('.pptx')) return '📊';
    if (url.includes('.pdf')) return '📄';
    return '📄';
  };

  const sendMessage = async (messageText) => {
    if (!messageText.trim()) return;
    reportData(2, messageText);
    const userMessage = {
      id: Date.now() + Math.random(),
      type: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setFollowUpQuestions([]);
    setLoading(true);
    setLoadingPhase(0);

    // Start the loading sequence
    let phaseInterval = setInterval(() => {
      setLoadingPhase(prev => {
        if (prev < 3) {
          return prev + 1;
        } else {
          clearInterval(phaseInterval);
          return prev;
        }
      });
    }, 10000); // 10 seconds per phase (40 seconds total)

    try {
      // Prepare conversation history
      const conversationHistory = messages.slice(-6).map(msg => ({
        type: msg.type,
        content: msg.content,
        explanation: msg.content,
        data: msg.data,
        sqlQuery: msg.sqlQuery
      }));

              const response = await axios.post('/api/jobsite/analyze', {
        question: messageText
      });

      // Check if we have a fallback message for data integration issues
      if (response.data.hasFallback && response.data.fallbackMessage) {
        const fallbackMessage = {
          id: Date.now() + Math.random() + 1,
          type: 'bot',
          content: response.data.fallbackMessage,
          error: false,
          timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, fallbackMessage]);
        toast.info('Data integration in progress');
        return;
      }

      if (response.data.success) {
        // Use the raw SQL data directly - no more hard-coded processing
        let displayData = response.data.data.rawData || response.data.data || [];
        let columns = displayData.length > 0 ? Object.keys(displayData[0]) : [];

        // Debug: Log the response data to see what we're receiving
        console.log('🔍 Response data:', {
          explanation: response.data.explanation ? 'Present' : 'Missing',
          sqlQuery: response.data.sqlQuery ? 'Present' : 'Missing',
          sqlRationale: response.data.sqlRationale ? 'Present' : 'Missing',
          sqlRationaleLength: response.data.sqlRationale ? response.data.sqlRationale.length : 0
        });

        const botMessage = {
          id: Date.now() + Math.random() + 1,
          type: 'bot',
          content: response.data.explanation,
          data: displayData,
          sqlQuery: response.data.sqlQuery,
          sqlRationale: response.data.sqlRationale,
          columns: columns,
          rowCount: response.data.rowCount,
          assetUrls: [],
          timestamp: new Date().toISOString()
        };

        // Debug: Log the message object to see what we're setting
        console.log('🔍 Bot message:', {
          sqlRationale: botMessage.sqlRationale ? 'Present' : 'Missing',
          sqlRationaleLength: botMessage.sqlRationale ? botMessage.sqlRationale.length : 0
        });

        setMessages(prev => [...prev, botMessage]);
        
        // Use dynamic follow-up questions from backend
        if (response.data.followUpQuestions && response.data.followUpQuestions.length > 0) {
          setFollowUpQuestions(response.data.followUpQuestions);
        } else {
          // Fallback to static questions if dynamic ones aren't available
          const fallbackQuestions = [
            "Show me budget vs actual costs by phase for Jobsite 25-111",
            "What are the labor costs by craft for Jobsite 25-111?",
            "Show me costs by cost type for Jobsite 25-480"
          ];
          setFollowUpQuestions(fallbackQuestions);
        }
        
        toast.success('Analysis completed successfully!');
      } else {
        const errorMessage = {
          id: Date.now() + Math.random() + 1,
          type: 'bot',
          content: response.data.message || 'Sorry, I couldn\'t process your request.',
          error: true,
          timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, errorMessage]);
        toast.error('Failed to process query');
      }
    } catch (error) {
      const errorMessage = {
        id: Date.now() + Math.random() + 1,
        type: 'bot',
        content: 'Sorry, I encountered an error. Please try again.',
        error: true,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, errorMessage]);
      toast.error('Network error occurred');
    } finally {
      setLoading(false);
      setLoadingPhase(0);
      if (phaseInterval) {
        clearInterval(phaseInterval);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };



  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatPercentage = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  const formatSQL = (sql) => {
    if (!sql) return '';
    return sql
      .replace(/SELECT/gi, '\nSELECT')
      .replace(/FROM/gi, '\nFROM')
      .replace(/WHERE/gi, '\nWHERE')
      .replace(/GROUP BY/gi, '\nGROUP BY')
      .replace(/ORDER BY/gi, '\nORDER BY')
      .replace(/LIMIT/gi, '\nLIMIT');
  };

  const getLoadingMessage = () => {
    const messages = [
      "Analyzing jobsite data...",
      "Generating insights...",
      "Preparing visualizations...",
      "Almost done..."
    ];
    return messages[loadingPhase] || messages[0];
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 flex flex-col gap-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <h1 className="text-2xl font-bold text-gray-900">AI Vista Analyst</h1>
                <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                  auto-refresh nightly at 12 AM
                </span>
              </div>
              <p className="text-gray-600">AI-powered Vista construction finance data analysis and insights</p>
            </div>
          </div>
        </div>

        {/* Chat Interface */}
        <div className="w-full flex-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-[700px] flex flex-col">
            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-8">
              
              {messages.length === 0 && (
                <div className="text-center py-8">
                  
                  {/* Role Selection - Show only for non-personalized users (same as main branch) */}
                  {!isPersonalized && (
                    <div className="mb-4">
                      <p className="text-gray-600 mb-3">Select your role for tailored Vista financial questions:</p>
                      <div className="flex flex-wrap gap-2 justify-center mb-4">
                        {Object.keys(roleQuestions).map((role) => (
                          <button
                            key={role}
                            onClick={() => setSelectedRole(role)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                              selectedRole === role
                                ? 'bg-sylvan-600 text-white shadow-sm'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {role === 'Exec' ? 'Executive' : 
                             role === 'PM' ? 'Project Manager' :
                             role === 'Division Lead' ? 'Division Lead' :
                             role === 'Finance' ? 'Finance' : role}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Default Questions */}
                  <div className="max-w-lg mx-auto">
                    <h4 className="text-md font-medium text-gray-800 mb-3">
                      Suggested Questions 
                      {/* {isPersonalized 
                        ? 'Suggested Questions' 
                        : `Suggested Questions for ${selectedRole === 'Exec' ? 'Executive' : 
                                                     selectedRole === 'PM' ? 'Project Manager' : selectedRole}`} */}
                    </h4>
                    <div className="grid grid-cols-1 gap-2">
                      {defaultQuestions.map((question, index) => (
                        <button
                          key={index}
                          onClick={() => sendMessage(question)}
                          className={`text-left p-3 border border-gray-200 rounded-lg text-sm transition-colors ${
                            isPersonalized 
                              ? 'bg-sylvan-50 hover:bg-sylvan-100 hover:border-sylvan-400 text-sylvan-800 border-sylvan-200'
                              : 'bg-gray-50 hover:bg-sylvan-50 hover:border-sylvan-300 text-gray-700'
                          }`}
                        >
                          {question}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {messages.map((message, index) => (
                <div
                  key={message.id}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-4xl w-full rounded-lg px-4 py-3 ${
                      message.type === 'user'
                        ? 'bg-sylvan-600 text-white'
                        : message.error
                        ? 'bg-red-50 text-red-800 border border-red-200'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    <div className="flex items-start space-x-2">
                      {message.type === 'bot' && (
                        <Bot className="h-5 w-5 text-sylvan-600 mt-0.5 flex-shrink-0" />
                      )}
                      {message.type === 'user' && (
                        <User className="h-5 w-5 text-white mt-0.5 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm break-words prose prose-sm max-w-none">
                          <div dangerouslySetInnerHTML={{ 
                            __html: (message.content || 'No response available')
                              .replace(/## (.*?)\n/g, '<h3 class="text-lg font-bold text-gray-900 mt-6 mb-3 border-b border-gray-200 pb-2">$1</h3>')
                              .replace(/### (.*?)\n/g, '<h4 class="text-md font-semibold text-gray-800 mt-4 mb-2">$1</h4>')
                              .replace(/- (.*?)\n/g, '<li class="ml-4 mb-2 text-gray-700 leading-relaxed">• $1</li>')
                              .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>')
                              .replace(/\*(.*?)\*/g, '<em class="italic text-gray-700">$1</em>')
                              .replace(/\n\n/g, '<br/><br/>')
                              .replace(/\n/g, '<br/>')
                          }} />
                        </div>
                        
                        {/* SQL Rationale - Always visible when available */}
                        {(() => {
                          console.log('🔍 Checking SQL rationale for message:', message.id, {
                            hasSqlRationale: !!message.sqlRationale,
                            sqlRationaleLength: message.sqlRationale ? message.sqlRationale.length : 0
                          });
                          return message.sqlRationale && (
                            <div className="mt-3">
                              <div className="bg-sylvan-50 rounded-lg border border-sylvan-200">
                                <div className="p-4">
                                  <h4 className="text-sm font-bold text-sylvan-800 mb-3 flex items-center">
                                    <Code className="h-4 w-4 mr-2" />
                                    SQL Query Breakdown
                                  </h4>
                                  <div className="prose prose-sm max-w-none">
                                    <div dangerouslySetInnerHTML={{ 
                                      __html: (message.sqlRationale || 'No SQL rationale available')
                                        .replace(/## (.*?)\n/g, '<h4 class="text-md font-bold text-sylvan-800 mt-4 mb-3 border-b border-sylvan-200 pb-1">$1</h4>')
                                        .replace(/### (.*?)\n/g, '<h5 class="text-sm font-semibold text-sylvan-700 mt-3 mb-2">$1</h5>')
                                        .replace(/- (.*?)\n/g, '<li class="ml-4 mb-2 text-blue-700 leading-relaxed">• $1</li>')
                                        .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-blue-900">$1</strong>')
                                        .replace(/\*(.*?)\*/g, '<em class="italic text-blue-700">$1</em>')
                                        .replace(/```sql\n(.*?)\n```/gs, '<div class="bg-blue-100 p-2 rounded text-xs font-mono text-blue-900 my-2">$1</div>')
                                        .replace(/\n\n/g, '<br/><br/>')
                                        .replace(/\n/g, '<br/>')
                                    }} />
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                        {/* SQL Query Display - Collapsible */}
                        {message.sqlQuery && (
                          <div className="mt-3">
                            <div className="bg-gray-50 rounded-lg border border-gray-200">
                              {/* Collapsible SQL Query */}
                              <div className="p-3">
                                <button
                                  onClick={() => setExpandedSQL(prev => ({ ...prev, [message.id]: !prev[message.id] }))}
                                  className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 hover:text-gray-900"
                                >
                                  <span className="flex items-center">
                                    <Code className="h-4 w-4 mr-2" />
                                    {expandedSQL[message.id] ? 'Hide SQL Query' : 'Show SQL Query'}
                                  </span>
                                  <ChevronDown className={`h-4 w-4 transition-transform ${expandedSQL[message.id] ? 'rotate-180' : ''}`} />
                                </button>
                                
                                {expandedSQL[message.id] && (
                                  <div className="mt-3 p-3 bg-gray-800 rounded text-green-400 text-xs font-mono">
                                    <div className="overflow-x-auto">
                                      <pre className="whitespace-pre-wrap break-words">{formatSQL(message.sqlQuery)}</pre>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Data Results */}
                        {message.data && message.data.length > 0 && (
                          <div className="mt-3">
                            <div className="overflow-x-auto">
                              <DataTable 
                                data={message.data} 
                                columns={message.columns} 
                              />
                              
                              {/* Export Buttons - CSV and Slides */}
                              <div className="mt-3 flex justify-end space-x-2">
                                {/* CSV Export Button */}
                                <button
                                  onClick={() => exportToCSV(message.data, message.columns, message.content)}
                                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sylvan-500"
                                  title="Export data to CSV"
                                >
                                  <Download size={16} className="mr-2" />
                                  CSV
                                </button>
                                
                                {/* Slides Button - Show initially, then replace with Web/Download */}
                                {!gammaProgress[index] || !gammaProgress[index].completed ? (
                                  <button
                                    onClick={() => generateGammaSlides(message, index)}
                                    disabled={gammaLoading[index]}
                                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sylvan-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Generate beautiful slides with Gamma AI"
                                  >
                                    {gammaLoading[index] ? (
                                      <Loader2 size={16} className="mr-2 animate-spin" />
                                    ) : (
                                      <Presentation size={16} className="mr-2" />
                                    )}
                                    {gammaLoading[index] ? 'Generating...' : 'Slides'}
                                  </button>
                                ) : (
                                  // Show Download (PPTX) only; Web button removed
                                  <div className="flex space-x-2">
                                    {(gammaProgress[index]?.exportUrl || message.exportUrl) && (
                                      <a
                                        href={gammaProgress[index]?.exportUrl || message.exportUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
                                        title="Open PPTX in new tab"
                                      >
                                        📊 PPTX
                                      </a>
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              {/* Progress Display - Simple */}
                              {gammaLoading[index] && (
                                <div className="mt-2 text-sm text-gray-600">
                                  <div className="flex items-center justify-between mb-2">
                                    <span>⏱️ {gammaProgress[index]?.elapsed || 0}s elapsed</span>
                                    <span>⏳ {gammaProgress[index]?.remaining || 120}s remaining</span>
                                  </div>
                                  <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div 
                                      className="bg-sylvan-600 h-2 rounded-full transition-all duration-300 ease-out" 
                                      style={{ width: `${gammaProgress[index]?.progress || 0}%` }}
                                    ></div>
                                  </div>
                                </div>
                              )}
                              
                              {/* Success Message */}
                              {gammaProgress[index]?.completed && !gammaLoading[index] && (
                                <div className="mt-2 text-sm text-green-600 text-center">
                                  <span>✅ Slides ready! Check your downloads</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Asset Grid */}
                        {message.assetUrls && message.assetUrls.length > 0 && (
                          <AssetGrid 
                            assets={message.assetUrls}
                            title="Related Assets"
                          />
                        )}

                        {/* Voting Component for Bot Messages */}
                        {message.type === 'bot' && (
                          <div className="mt-4 pt-3 border-t border-gray-200 mb-2">
                            <MessageVoting
                              messageId={message.id}
                              userVote={userVotes[message.id]}
                              onVote={handleVote}
                              onFeedback={openFeedbackModal}
                            />
                          </div>
                        )}

                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-lg px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <Loader2 className="h-5 w-5 text-gray-500 animate-spin" />
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-600">
                          {loadingPhase === 0 && "Analyzing your question..."}
                          {loadingPhase === 1 && "Creating query..."}
                          {loadingPhase === 2 && "Getting response..."}
                          {loadingPhase === 3 && "Summarizing answer..."}
                        </span>
                        <div className="flex space-x-1 mt-1">
                          {[0, 1, 2, 3].map((phase) => (
                            <div
                              key={phase}
                              className={`h-1 w-8 rounded-full transition-all duration-300 ${
                                phase <= loadingPhase ? 'bg-sylvan-500' : 'bg-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input Form */}
            <div className="border-t border-gray-200 p-4">
              <form onSubmit={handleSubmit} className="flex space-x-3">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about jobsite job cost related questions..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sylvan-500 focus:border-transparent"
                  disabled={loading}
                />
                <button
                  type="submit"
                  disabled={loading || !input.trim()}
                  className="px-4 py-2 bg-sylvan-600 text-white rounded-lg hover:bg-sylvan-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  <Send className="h-4 w-4" />
                  <span>Send</span>
                </button>
              </form>
              
              {/* Follow-up Questions */}
              {followUpQuestions.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100 mb-2">
                  <div className="flex items-center space-x-2 mb-3">
                    <ChevronRight className="h-4 w-4 text-sylvan-600" />
                    <span className="text-sm font-medium text-gray-700">Follow-up questions:</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {followUpQuestions.map((question, index) => (
                      <button
                        key={index}
                        onClick={() => sendMessage(question)}
                        className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-full text-xs font-medium transition-colors border border-blue-200"
                      >
                        {question}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={feedbackModal.isOpen}
        onClose={closeFeedbackModal}
        onSubmit={handleFeedback}
        messageId={feedbackModal.messageId}
        voteType={feedbackModal.voteType}
        userQuestion={messages.find(m => m.id === feedbackModal.messageId)?.content || ''}
      />
    </div>
  );
};

export default JobsiteDeepDive; 